import type { RefObject } from 'react';
import { ChevronDown, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { MathMarkdown } from '@/components/chat/MathMarkdown';
import { Skeleton } from '@/components/ui/skeleton';
import { EmoviDocMessage } from './EmoviDocMessage';
import type { EmoviDocMessage as EmoviDocMessageType } from './types';

type EmoviDocMessageListProps = {
  loading: boolean;
  messages: EmoviDocMessageType[];
  isStreaming: boolean;
  streamingText: string;
  streamingReasoning: string;
  showScrollButton: boolean;
  onScroll: () => void;
  onScrollToBottom: () => void;
  messagesContainerRef: RefObject<HTMLDivElement | null>;
  bottomAnchorRef: RefObject<HTMLDivElement | null>;
};

export function EmoviDocMessageList({
  loading,
  messages,
  isStreaming,
  streamingText,
  streamingReasoning,
  showScrollButton,
  onScroll,
  onScrollToBottom,
  messagesContainerRef,
  bottomAnchorRef,
}: EmoviDocMessageListProps) {
  return (
    <div className="relative flex-1 min-h-0">
      <div
        ref={messagesContainerRef}
        className="h-full overflow-y-auto px-4 py-4 space-y-3"
        onScroll={onScroll}
      >
        {loading ? (
          <>
            <Skeleton className="h-10 w-[75%] rounded-2xl ml-auto" />
            <Skeleton className="h-8 w-[65%] rounded-lg" />
            <Skeleton className="h-8 w-[70%] rounded-lg" />
          </>
        ) : null}

        {!loading && messages.length === 0 && !isStreaming ? (
          <EmptyState
            icon={<Sparkles className="h-8 w-8" />}
            title="Cosa vuoi sapere?"
            description="Chiedi informazioni su diari, chat, note cliniche, trend e review del cliente."
          />
        ) : null}

        {!loading
          ? messages.map((message) => <EmoviDocMessage key={message.id} message={message} />)
          : null}

        {isStreaming ? (
          <div className="space-y-2">
            {streamingReasoning ? (
              <div className="bg-muted/30 rounded-lg px-3 py-2 text-xs text-muted-foreground">
                <p className="font-medium mb-1 flex items-center gap-1">
                  <ChevronDown className="h-3.5 w-3.5" />
                  Ragionamento
                </p>
                <div className="prose prose-sm max-w-none text-muted-foreground">
                  <MathMarkdown>{streamingReasoning}</MathMarkdown>
                </div>
              </div>
            ) : null}
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                EmoviDoc sta elaborando...
              </div>
              {streamingText ? (
                <div className="prose prose-sm max-w-none text-foreground">
                  <MathMarkdown>{streamingText}</MathMarkdown>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        <div ref={bottomAnchorRef} />
      </div>

      {showScrollButton ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="absolute bottom-4 right-4"
          onClick={onScrollToBottom}
        >
          Scorri giù
        </Button>
      ) : null}
    </div>
  );
}
